import { motion } from 'framer-motion';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components';

import { tokens } from '../theme/tokens';

const TooltipContainer = styled.div`
	position: relative;
	display: inline-block;
`;

type TooltipPosition = 'top' | 'bottom' | 'left' | 'right' | 'center';
type TooltipPropPosition = TooltipPosition | 'auto';

const positionStyles: Record<TooltipPosition, ReturnType<typeof css>> = {
	top: css`
		bottom: 100%;
		left: 50%;
		transform: translateX(calc(-50% + var(--shift-x, 0px)));
		margin-bottom: 8px;
	`,
	bottom: css`
		top: 100%;
		left: 50%;
		transform: translateX(calc(-50% + var(--shift-x, 0px)));
		margin-top: 8px;
	`,
	left: css`
		right: 100%;
		top: 50%;
		transform: translateY(-50%);
		margin-right: 8px;
	`,
	right: css`
		left: 100%;
		top: 50%;
		transform: translateY(-50%);
		margin-left: 8px;
	`,
	center: css`
		left: 50%;
		top: 50%;
		transform: translateX(calc(-50% + var(--shift-x, 0px))) translateY(-50%);
		margin-left: 8px;
	`,
};

// The arrow overlaps the box by 1px: on a fractional position (portal mode
// transforms) a hairline seam would otherwise show between the two.
const arrowStyles: Record<TooltipPosition, ReturnType<typeof css>> = {
	top: css`
		top: calc(100% - 1px);
		left: 50%;
		transform: translateX(calc(-50% - var(--shift-x, 0px)));
		border: 6px solid transparent;
		border-top-color: var(--tooltip-bg, white);
	`,
	bottom: css`
		bottom: calc(100% - 1px);
		left: 50%;
		transform: translateX(calc(-50% - var(--shift-x, 0px)));
		border: 6px solid transparent;
		border-bottom-color: var(--tooltip-bg, white);
	`,
	left: css`
		left: calc(100% - 1px);
		top: 50%;
		transform: translateY(-50%);
		border: 6px solid transparent;
		border-left-color: var(--tooltip-bg, white);
	`,
	right: css`
		right: calc(100% - 1px);
		top: 50%;
		transform: translateY(-50%);
		border: 6px solid transparent;
		border-right-color: var(--tooltip-bg, white);
	`,
	center: css`
		left: 50%;
		top: 50%;
		transform: translateX(-50%) translateY(-50%);
		border: 6px solid transparent;
	`,
};

/*
 * Positioning shell: anchored next to the trigger with a CSS transform (or,
 * in portal mode, imperatively). The visible box is the child, so its enter
 * and leave animation can use transforms of its own.
 */
const TooltipContent = styled.div<{
	position: TooltipPosition;
	floating?: boolean;
}>`
	position: ${({ floating }) => (floating ? 'fixed' : 'absolute')};
	${({ position, floating }) => !floating && positionStyles[position]}
	z-index: 1000;
	pointer-events: none;
`;

/** Where the box grows from: the side its arrow points to. */
const transformOrigins: Record<TooltipPosition, string> = {
	top: 'bottom center',
	bottom: 'top center',
	left: 'right center',
	right: 'left center',
	center: 'center',
};

/** Hidden state per position: slightly smaller, faded, and nudged towards the trigger. */
const hiddenOffsets: Record<TooltipPosition, { x: number; y: number }> = {
	top: { x: 0, y: 3 },
	bottom: { x: 0, y: -3 },
	left: { x: 3, y: 0 },
	right: { x: -3, y: 0 },
	center: { x: 0, y: 0 },
};

const boxVariants = {
	hidden: (position: TooltipPosition) => ({
		opacity: 0,
		scale: 0.9,
		pointerEvents: 'none' as const,
		...hiddenOffsets[position],
	}),
	shown: { opacity: 1, scale: 1, x: 0, y: 0, pointerEvents: 'auto' as const },
};

const BOX_TRANSITION = { duration: 0.2, ease: [0.16, 1, 0.3, 1] as const };

const TooltipBox = styled(motion.div)<{
	position: TooltipPosition;
	fullWidth?: boolean;
	fitContent?: boolean;
	borderColor?: string;
	hideArrow?: boolean;
	dark?: boolean;
}>`
	position: relative;
	background: white;
	border: 1px solid #dae0e8;
	${(props) => props.borderColor && `border-color: ${props.theme.colors[props.borderColor]};`}
	border-radius: 8px;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
	padding: 12px;
	${({ dark }) =>
		dark &&
		css`
			--tooltip-bg: ${tokens.surface.sidebar};
			background: ${tokens.surface.sidebar};
			color: ${tokens.text.inverse};
			border: none;
			padding: ${tokens.gap.inline}px ${tokens.padding.control}px;
			max-width: 300px;
			font-family: ${tokens.typography.fontFamily};
			font-size: ${tokens.typography.sizes.body}px;
			font-weight: ${tokens.typography.weights.regular};
			line-height: ${tokens.typography.lineHeights.body};
			white-space: normal;
			text-align: left;
		`}
	min-width: ${({ fitContent }) => (fitContent ? '0' : '200px')};
	${(props) =>
		!props.fullWidth &&
		`
		max-width: 400px;
	`}
	width: max-content;
	word-wrap: break-word;
	white-space: pre-line;

	&::after {
		${({ hideArrow }) => hideArrow && 'display: none;'}
		content: '';
		position: absolute;
		${({ position }) => arrowStyles[position]}
	}
`;

// Transforms used when the tooltip is rendered in a portal (position: fixed),
// so the gap and anchoring match the absolute-positioned variant.
const floatingTransforms: Record<TooltipPosition, string> = {
	top: 'translate(-50%, -100%)',
	bottom: 'translateX(-50%)',
	left: 'translate(-100%, -50%)',
	right: 'translateY(-50%)',
	center: 'translate(-50%, -50%)',
};

// Minimum gap kept between the tooltip and the viewport edges.
const VIEWPORT_MARGIN = 16;

// Returns how far a horizontally-centered tooltip must be shifted so it stays
// within the viewport. Negative shifts it left (away from the right edge),
// positive shifts it right (away from the left edge).
function getHorizontalShift(centerX: number, width: number, margin = VIEWPORT_MARGIN) {
	const halfWidth = width / 2;
	const leftEdge = centerX - halfWidth;
	const rightEdge = centerX + halfWidth;
	const maxRight = window.innerWidth - margin;

	if (rightEdge > maxRight) {
		return maxRight - rightEdge;
	}
	if (leftEdge < margin) {
		return margin - leftEdge;
	}
	return 0;
}

function isHorizontallyCentered(position: TooltipPosition) {
	return position === 'top' || position === 'bottom' || position === 'center';
}

function getFloatingCoords(rect: DOMRect, position: TooltipPosition) {
	const gap = 8;
	const centerX = rect.left + rect.width / 2;
	const centerY = rect.top + rect.height / 2;

	switch (position) {
		case 'top':
			return { left: centerX, top: rect.top - gap };
		case 'bottom':
			return { left: centerX, top: rect.bottom + gap };
		case 'left':
			return { left: rect.left - gap, top: centerY };
		case 'right':
			return { left: rect.right + gap, top: centerY };
		case 'center':
		default:
			return { left: centerX, top: centerY };
	}
}

type Props = {
	children: React.ReactNode;
	content: React.ReactNode;
	enabled?: boolean;
	position?: TooltipPropPosition;
	align?: TooltipPosition;
	fullWidth?: boolean;
	/** Let the tooltip shrink to its content instead of the 200px minimum. */
	fitContent?: boolean;
	borderColor?: string;
	hideArrow?: boolean;
	/** Dark surface with inverse text and compact padding. */
	dark?: boolean;
	className?: string;
	/**
	 * Render the tooltip content in a portal on `document.body` using fixed
	 * positioning. Use this when the tooltip would otherwise be clipped by an
	 * ancestor with `overflow: hidden` (e.g. inside an AG Grid header).
	 */
	portal?: boolean;
};

function getBestTooltipPosition(
	containerRect: DOMRect,
	tooltipRect: DOMRect,
	overrideAlign?: TooltipPosition,
): TooltipPosition {
	const tooltipHeight = tooltipRect.height + 16; // margin included
	const tooltipWidth = tooltipRect.width + 16;

	const spaceAbove = containerRect.top;
	const spaceBelow = window.innerHeight - containerRect.bottom;
	const spaceLeft = containerRect.left;
	const spaceRight = window.innerWidth - containerRect.right;

	// Priority order: top > bottom > right > left
	const candidates: { position: TooltipPosition; fits: boolean }[] = [
		{ position: 'top', fits: spaceAbove >= tooltipHeight },
		{ position: 'bottom', fits: spaceBelow >= tooltipHeight },
		{ position: 'right', fits: spaceRight >= tooltipWidth },
		{ position: 'left', fits: spaceLeft >= tooltipWidth },
		{
			position: 'center',
			fits:
				spaceAbove >= tooltipHeight &&
				spaceBelow >= tooltipHeight &&
				spaceRight >= tooltipWidth &&
				spaceLeft >= tooltipWidth,
		},
	];

	if (overrideAlign) {
		return candidates[overrideAlign].position;
	}

	const optimalCandidate = candidates.find(({ fits }) => fits) || candidates[0];

	return optimalCandidate.position;
}

export function Tooltip({
	children,
	content,
	enabled = true,
	position = 'auto',
	fullWidth = false,
	fitContent = false,
	borderColor = undefined,
	hideArrow = false,
	dark = false,
	align = undefined,
	className,
	portal = false,
}: Props) {
	const [isVisible, setIsVisible] = useState(false);
	const [isMounted, setIsMounted] = useState(false);
	const [calculatedPosition, setCalculatedPosition] = useState<TooltipPosition>('top');
	const [shiftX, setShiftX] = useState(0);
	const containerRef = useRef<HTMLDivElement>(null);
	const tooltipRef = useRef<HTMLDivElement>(null);

	// Keep the tooltip node mounted for a short window after hide so the
	// opacity/visibility transition can still play out, then unmount the
	// content subtree so it stops paying rendering cost while not in use.
	useEffect(() => {
		if (isVisible) {
			setIsMounted(true);
			return;
		}
		const timeout = setTimeout(() => setIsMounted(false), 250);
		return () => clearTimeout(timeout);
	}, [isVisible]);

	useLayoutEffect(() => {
		if (!isMounted || !isVisible || position !== 'auto') {
			return;
		}

		const container = containerRef.current;
		const tooltip = tooltipRef.current;
		if (!container || !tooltip) {
			return;
		}

		const containerRect = container.getBoundingClientRect();
		const tooltipRect = tooltip.getBoundingClientRect();

		setCalculatedPosition(getBestTooltipPosition(containerRect, tooltipRect, align));
	}, [isMounted, isVisible, position, align]);

	const finalPosition: TooltipPosition = position === 'auto' ? calculatedPosition : position;

	// In portal mode the content is fixed-positioned on the body. We position it
	// imperatively from the anchor's live viewport rect on every animation frame,
	// so it keeps following the anchor regardless of which element scrolls. The
	// first sync runs synchronously (before paint) to avoid a wrong first frame.
	useLayoutEffect(() => {
		if (!portal || !isMounted) {
			return;
		}

		let frame = 0;

		const sync = () => {
			const container = containerRef.current;
			const tooltip = tooltipRef.current;
			if (container && tooltip) {
				const { left, top } = getFloatingCoords(container.getBoundingClientRect(), finalPosition);
				const shift = isHorizontallyCentered(finalPosition)
					? getHorizontalShift(left, tooltip.offsetWidth)
					: 0;
				tooltip.style.left = `${left + shift}px`;
				tooltip.style.top = `${top}px`;
				tooltip.style.transform = floatingTransforms[finalPosition];
				tooltip.style.setProperty('--shift-x', `${shift}px`);
			}
			frame = requestAnimationFrame(sync);
		};

		sync();

		return () => cancelAnimationFrame(frame);
	}, [portal, isMounted, finalPosition]);

	// In non-portal (absolute) mode the tooltip is centered on its anchor with a
	// CSS transform. Compute a horizontal shift so a `top`/`bottom`/`center`
	// tooltip never overflows the viewport edges; the arrow compensates so it
	// keeps pointing at the anchor.
	useLayoutEffect(() => {
		if (portal || !isMounted || !isVisible) {
			return;
		}

		const container = containerRef.current;
		const tooltip = tooltipRef.current;
		if (!container || !tooltip) {
			return;
		}

		if (!isHorizontallyCentered(finalPosition)) {
			setShiftX(0);
			return;
		}

		const computeShift = () => {
			const rect = container.getBoundingClientRect();
			const centerX = rect.left + rect.width / 2;
			setShiftX(getHorizontalShift(centerX, tooltip.offsetWidth));
		};

		computeShift();

		window.addEventListener('resize', computeShift);
		window.addEventListener('scroll', computeShift, true);
		return () => {
			window.removeEventListener('resize', computeShift);
			window.removeEventListener('scroll', computeShift, true);
		};
	}, [portal, isMounted, isVisible, finalPosition, content]);

	if (!enabled) {
		return <>{children}</>;
	}

	const tooltipContent = (
		<TooltipContent
			ref={tooltipRef}
			position={finalPosition}
			floating={portal}
			style={portal ? undefined : ({ '--shift-x': `${shiftX}px` } as React.CSSProperties)}
		>
			<TooltipBox
				position={finalPosition}
				fullWidth={fullWidth}
				fitContent={fitContent}
				borderColor={borderColor}
				hideArrow={hideArrow}
				dark={dark}
				custom={finalPosition}
				variants={boxVariants}
				initial="hidden"
				animate={isVisible ? 'shown' : 'hidden'}
				transition={BOX_TRANSITION}
				style={{
					transformOrigin: transformOrigins[finalPosition],
					pointerEvents: isVisible ? 'auto' : 'none',
				}}
			>
				{content}
			</TooltipBox>
		</TooltipContent>
	);

	return (
		<TooltipContainer
			ref={containerRef}
			onMouseEnter={() => setIsVisible(true)}
			onMouseLeave={() => setIsVisible(false)}
			className={className}
		>
			{children}
			{isMounted && (portal ? createPortal(tooltipContent, document.body) : tooltipContent)}
		</TooltipContainer>
	);
}
